package one.devsbox.www.ui.campaigns.detail

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridItemSpan
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.ChevronRight
import kotlinx.datetime.LocalDate
import one.devsbox.commons.EMPTY
import one.devsbox.shared.ads.CampaignState
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.campaigns.color
import one.devsbox.www.ui.campaigns.icon
import one.devsbox.www.ui.resources.MediaCard
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode(ArgsId::class)
fun CampaignDUI() {
    with(CampaignDCtx.get()) {
        ContentCampaignDUI()
    }
}

@Composable
context(ctx: CampaignDCtx)
private fun ContentCampaignDUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> {
                    SuccessUI(state.nnvl.info)
                }
            }
        }
    }
}

context(ctx: CampaignDCtx) @Composable
private fun SuccessUI(info: SharedIO.CampaignInfo) {
    Box(Modifier.fillMaxSize()) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            val isWide = maxWidth >= 1000.dp

            LazyVerticalStaggeredGrid(
                columns = StaggeredGridCells.Fixed(if (isWide) 2 else 1),
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(
                    start = 16.dp,
                    top = 16.dp,
                    end = 16.dp,
                    bottom = 250.dp
                ),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalItemSpacing = 16.dp
            ) {
                // Header Section
                item(span = StaggeredGridItemSpan.FullLine) {
                    CampaignHeaderSection(info)
                }

                // Overview Section
                item(span = StaggeredGridItemSpan.FullLine) {
                    CampaignOverviewSection(info)
                }

                // Schedule Section
                item {
                    CampaignScheduleSection(info)
                }

                // Media Assets Section
                item {
                    CampaignMediaSection(info)
                }

                // Locations Section
                item {
                    CampaignLocationsSection(info)
                }
            }
        }
        ContentActions(
            contentActions = ctx.vm.contentActions,
            colorInactive = ctx.colorSecondaryContainer,
            colorActive = ctx.colorPrimaryContainer
        )
    }
}

context(ctx: CampaignDCtx) @Composable
private fun CampaignHeaderSection(info: SharedIO.CampaignInfo) {
    val isPortrait = ctx.dp.state.portrait || ctx.dp.state.smallScreen
    if (isPortrait) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                text = info.name.uppercase(),
                style = MaterialTheme.typography.displaySmall,
                fontWeight = FontWeight.Bold,
                letterSpacing = 1.sp,
                color = MaterialTheme.colorScheme.onSurface
            )

            val campaignState = CampaignState[info.state] ?: CampaignState.DRAFT
            StateBadge(campaignState)
        }
    } else {
        Row(
            modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = info.name.uppercase(),
                style = MaterialTheme.typography.displaySmall,
                fontWeight = FontWeight.Bold,
                letterSpacing = 1.sp,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.weight(1F)
            )

            val campaignState = CampaignState[info.state] ?: CampaignState.DRAFT
            StateBadge(campaignState)
        }
    }
}

context(ctx: CampaignDCtx) @Composable
private fun CampaignOverviewSection(info: SharedIO.CampaignInfo) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        LabelSection(stringResource(Res.string.ui_campaigns_detail_overview))

        val isPortrait = ctx.dp.state.portrait || ctx.dp.state.smallScreen
        if (isPortrait) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    OverviewCard(
                        label = stringResource(Res.string.ui_campaigns_detail_reach_estimate),
                        value = info.impressions.all.formatCount(),
                        modifier = Modifier.weight(1f)
                    )
                    OverviewCard(
                        label = stringResource(Res.string.ui_campaigns_detail_geo_nodes),
                        value = info.location.size.toString(),
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    OverviewCard(
                        label = stringResource(Res.string.ui_campaigns_detail_total_assets),
                        value = info.visual.size.toString(),
                        modifier = Modifier.weight(1f)
                    )
                    OverviewCard(
                        label = stringResource(Res.string.ui_campaigns_detail_device_cluster),
                        value = info.device.size.toString(),
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        } else {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                OverviewCard(
                    label = stringResource(Res.string.ui_campaigns_detail_reach_estimate),
                    value = info.impressions.all.formatCount(),
                    modifier = Modifier.weight(1f)
                )
                OverviewCard(
                    label = stringResource(Res.string.ui_campaigns_detail_geo_nodes),
                    value = info.location.size.toString(),
                    modifier = Modifier.weight(1f)
                )
                OverviewCard(
                    label = stringResource(Res.string.ui_campaigns_detail_total_assets),
                    value = info.visual.size.toString(),
                    modifier = Modifier.weight(1f)
                )
                OverviewCard(
                    label = stringResource(Res.string.ui_campaigns_detail_device_cluster),
                    value = info.device.size.toString(),
                    modifier = Modifier.weight(1f)
                )
            }
        }
    }
}

context(ctx: CampaignDCtx) @Composable
private fun CampaignMediaSection(info: SharedIO.CampaignInfo) {
    Column(
        modifier = Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        LabelSection(stringResource(Res.string.ui_campaigns_detail_media_assets))

        FlowRow(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            info.visual.forEach { media ->
                val enabled = ctx.me.permission.media.read
                Box(
                    modifier = Modifier.widthIn(max = 240.dp).weight(1f, fill = false)
                        .then(if (enabled) Modifier else Modifier.graphicsLayer { alpha = 0.5f })
                ) {

                    MediaCard(media, showDurationOnImage = true) {
                        if (enabled) ctx.action.itemClick(media)
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: CampaignDCtx)
private fun CampaignLocationsSection(info: SharedIO.CampaignInfo) {
    Column(
        modifier = Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        LabelSection(stringResource(Res.string.ui_campaigns_detail_locations))
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            info.location.forEach { loc ->
                val devicesInLocation = info.device.filter { it.id_location == loc.id }
                LocationExpandableItem(loc, devicesInLocation)
            }
        }
    }
}

context(ctx: CampaignDCtx) @Composable
private fun StateBadge(state: CampaignState) {
    Surface(
        color = Color.Transparent,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        shape = RoundedCornerShape(4.dp)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = stringResource(Res.string.ui_campaigns_detail_status),
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.align(Alignment.CenterVertically)
            )

            Spacer(Modifier.width(8.dp))
            Icon(
                imageVector = state.icon(),
                contentDescription = state.name,
                tint = state.color()
            )
            Spacer(Modifier.width(4.dp))
            Text(
                text = state.name,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
        }
    }
}

@Composable
private fun OverviewCard(label: String, value: String, modifier: Modifier = Modifier) {
    Card(
        modifier = modifier.height(96.dp),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(
            modifier = Modifier.fillMaxSize().padding(16.dp),
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                label,
                style = MaterialTheme.typography.labelLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                value,
                style = MaterialTheme.typography.displaySmall,
                fontWeight = FontWeight.Bold,
                fontFamily = FontFamily.Monospace,
                color = MaterialTheme.colorScheme.onSurface
            )
        }
    }
}


@Composable
context(ctx: CampaignDCtx)
private fun LocationExpandableItem(
    loc: SharedIO.CampaignInfo.Location,
    devices: List<SharedIO.CampaignInfo.Device>
) {
    var expanded by remember { mutableStateOf(false) }

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(IntrinsicSize.Min),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    modifier = Modifier
                        .weight(1f)
                        .clickable { expanded = !expanded }
                        .padding(20.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "${loc.id.toString().padStart(2, '0')} - ${loc.name.uppercase()}",
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodyLarge,
                        fontFamily = FontFamily.Monospace,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }

                VerticalDivider(
                    color = MaterialTheme.colorScheme.outlineVariant,
                    modifier = Modifier.padding(vertical = 8.dp)
                )

                val locEnabled = ctx.me.permission.location.read
                Box(
                    modifier = Modifier
                        .fillMaxHeight()
                        .then(if (locEnabled) Modifier.clickable { ctx.action.itemClick(loc) } else Modifier.graphicsLayer {
                            alpha = 0.5f
                        })
                        .padding(horizontal = 20.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = Svg.ChevronRight,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            AnimatedVisibility(visible = expanded) {
                Column(Modifier) {
                    devices.forEachIndexed { index, device ->
                        if (index != 0) HorizontalDivider(Modifier.padding(horizontal = 16.dp))
                        val devEnabled = ctx.me.permission.device.read
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .then(if (devEnabled) Modifier.clickable {
                                    ctx.action.itemClick(
                                        device
                                    )
                                } else Modifier.graphicsLayer {
                                    alpha = 0.5f
                                })
                                .padding(horizontal = 20.dp, vertical = 12.dp)
                                .padding(vertical = 8.dp),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Text(
                                device.name,
                                modifier = Modifier.padding(start = 16.dp)
                                    .align(Alignment.CenterVertically),
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurface,

                                )
                            InternetStatusBadge(
                                device.online,
                                modifier = Modifier.align(Alignment.CenterVertically)
                            )
                        }
                    }

                }
            }
        }
    }
}

context(ctx: CampaignDCtx) @Composable
private fun CampaignScheduleSection(info: SharedIO.CampaignInfo) {
    Column(
        modifier = Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        LabelSection(stringResource(Res.string.ui_campaigns_edit_temporal_parameters))

        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            ScheduleRow(
                label = stringResource(Res.string.ui_campaigns_edit_activation_date),
                value = if (info.dStart == LocalDate.EMPTY) stringResource(Res.string.ui_campaigns_edit_immediate) else info.dStart.toString()
            )
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            ScheduleRow(
                label = stringResource(Res.string.ui_campaigns_edit_expiration_date),
                value = if (info.dEnd == LocalDate.EMPTY) stringResource(Res.string.ui_campaigns_edit_perpetual) else info.dEnd.toString()
            )
        }
    }
}

@Composable
private fun ScheduleRow(label: String, value: String) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 4.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Bold,
            fontFamily = FontFamily.Monospace,
            color = MaterialTheme.colorScheme.onSurface
        )
    }
}

private fun Long.formatCount(): String {
    return when {
        this >= 1_000_000 -> "${(this / 1_000_000.0).let { (it * 10).toInt() / 10.0 }}M"
        this >= 1_000 -> "${(this / 1_000.0).let { (it * 10).toInt() / 10.0 }}K"
        else -> this.toString()
    }
}